/**
 * Session-memory store for unsent browser `File` objects. Redux holds only
 * serializable staged-attachment metadata; this registry keeps the bytes
 * keyed by `clientId` until the file is accepted, removed, or the owner
 * tab is disposed.
 *
 * Not a cache of persisted attachments. Accepted entries must be released
 * promptly. Never write these bytes to localStorage.
 */

import type { StagedAttachment } from '../slices/agent-orchestrator-slice'

const filesByClientId = new Map<string, File>()

function newClientId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()

  const bytes = new Uint8Array(16)
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
    .slice(6, 8)
    .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`
}

function metadataForFile(clientId: string, file: File): StagedAttachment {
  return {
    clientId,
    filename: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
    lastModified: file.lastModified,
    status: 'staged',
    error: null,
    errorCode: null,
    retryable: false
  }
}

/** Register one or more browser files and return serializable metadata. */
export function registerStagedAttachments(files: File[]): StagedAttachment[] {
  const registered: StagedAttachment[] = []
  for (const file of files) {
    const clientId = newClientId()
    filesByClientId.set(clientId, file)
    registered.push(metadataForFile(clientId, file))
  }
  return registered
}

/**
 * Resolve files in the caller-supplied client-ID order. Missing entries
 * are listed separately so the caller can fail visibly instead of sending
 * a shorter file list.
 */
export function resolveStagedAttachmentFiles(clientIds: string[]): {
  files: File[]
  missingClientIds: string[]
} {
  const files: File[] = []
  const missingClientIds: string[] = []
  for (const clientId of clientIds) {
    const file = filesByClientId.get(clientId)
    if (file === undefined) {
      missingClientIds.push(clientId)
    } else {
      files.push(file)
    }
  }
  return { files, missingClientIds }
}

/** Drop registry entries. Idempotent for unknown or already-released IDs. */
export function releaseStagedAttachments(clientIds: readonly string[]): void {
  for (const clientId of clientIds) {
    filesByClientId.delete(clientId)
  }
}

/** Look up one registry file. `undefined` when the ID was never registered or already released. */
export function getStagedAttachmentFile(clientId: string): File | undefined {
  return filesByClientId.get(clientId)
}

/** Test-only: drop every registry entry so specs cannot leak across cases. */
export function resetStagedAttachmentRegistry(): void {
  filesByClientId.clear()
}
